import { motion } from 'framer-motion';
import { ArrowRight, Package, Truck, Globe } from 'lucide-react';
import { Button } from './ui/button';

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

const stats = [
  { icon: Package, value: '10K+', label: 'Paquetes Enviados' },
  { icon: Truck, value: '4', label: 'Países de Destino' },
  { icon: Globe, value: '24/7', label: 'Soporte' },
];

export const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="inicio" 
      className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-white via-slate-50 to-white"
      data-testid="hero-section"
    >
      {/* Background decorations - pointer-events-none to avoid blocking clicks */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#2DD4BF]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-[#0066CC]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-[#F5A623]/10 rounded-full blur-2xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#2DD4BF]/10 text-[#0066CC] px-4 py-2 rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-[#2DD4BF] rounded-full animate-pulse" />
              <span className="font-medium text-sm">Envíos Internacionales</span>
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Envíos <span className="text-[#0066CC]">rápidos</span> y{' '}
              <span className="text-[#F5A623]">seguros</span> a tus países{' '}
              <span className="text-[#CC0000]">favoritos</span>
            </h1>
            
            <p className="text-lg text-slate-600 mb-8 max-w-lg mx-auto lg:mx-0">
              Conectamos Panamá, Venezuela, Colombia y República Dominicana con 
              el mejor servicio de courier. Tu paquete, nuestra prioridad.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={() => scrollToSection('#contacto')}
                className="bg-[#0066CC] hover:bg-[#0052a3] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all group"
                data-testid="hero-cta"
              >
                Cotizar Ahora
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={() => scrollToSection('#servicios')}
                className="border-2 border-slate-200 hover:border-[#0066CC] text-slate-700 hover:text-[#0066CC] rounded-full px-8 py-6 text-lg font-semibold transition-all"
                data-testid="hero-services"
              >
                Ver Destinos
              </Button>
            </div>
            
            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-slate-100"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <stat.icon className="w-6 h-6 text-[#2DD4BF] mb-2 mx-auto lg:mx-0" />
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Mascot */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Glow effect behind mascot */}
              <div className="absolute inset-0 bg-[#2DD4BF]/20 rounded-full blur-3xl scale-75" />
              
              {/* Mascot image */}
              <motion.img
                src={MASCOT_URL}
                alt="Mascota Buzón - Tecno Envios Express"
                className="relative z-10 w-72 sm:w-80 lg:w-96 h-auto mascot-shadow"
                animate={{ y: [0, -20, 0] }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
                data-testid="hero-mascot"
              />
              
              {/* Floating elements around mascot */}
              <motion.div
                className="absolute top-10 -left-4 md:-left-8 z-20 bg-white rounded-2xl shadow-lg p-3 flex items-center gap-2"
                animate={{ y: [0, -10, 0], rotate: [-2, 2, -2] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
              >
                <div className="w-8 h-8 bg-[#F5A623]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="w-4 h-4 text-[#F5A623]" />
                </div>
                <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Envío Seguro</span>
              </motion.div>
              
              <motion.div
                className="absolute bottom-16 -right-2 md:-right-6 z-20 bg-white rounded-2xl shadow-lg p-3 flex items-center gap-2"
                animate={{ y: [0, 10, 0], rotate: [2, -2, 2] }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
              >
                <div className="w-8 h-8 bg-[#0066CC]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Truck className="w-4 h-4 text-[#0066CC]" />
                </div>
                <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Entrega Rápida</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-slate-300 flex justify-center pt-2">
          <div className="w-1 h-2 bg-slate-400 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
